// src/components/Basicplan.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Helmet } from "react-helmet";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  Phone,
  Mail,
  Sparkles,
  Cake as CakeIcon,
  PartyPopper,
  Heart,
  Gift,
  GraduationCap,
  Baby,
  Smile,
  ShieldCheck,
  Lock,
  Tag,
  Info,
  ChevronDown,
  ChevronUp,
  X,
  QrCode,
  AlertCircle,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { URLS } from "../Url";

// Curated vegetarian fallback cakes
const FALLBACK_CAKES = [
  {
    _id: "fb_cake_1",
    name: "Royal Belgian Chocolate Truffle",
    image: "/img/c2.jpg",
    cakeType: "eggless",
    price: 650,
    category: "premium",
  },
  {
    _id: "fb_cake_2",
    name: "Red Velvet Romance Celebration",
    image: "/img/c1.jpg",
    cakeType: "eggless",
    price: 600,
    category: "premium",
  },
  {
    _id: "fb_cake_3",
    name: "Lotus Biscoff Caramel Elegance",
    image: "/img/c5.jpg",
    cakeType: "eggless",
    price: 700,
    category: "premium",
  },
  {
    _id: "fb_cake_4",
    name: "Ferrero Rocher Hazelnut Luxe",
    image: "/img/c6.jpg",
    cakeType: "eggless",
    price: 750,
    category: "premium",
  },
  {
    _id: "fb_cake_5",
    name: "Classic Dutch Chocolate",
    image: "/img/c3.jpg",
    cakeType: "eggless",
    price: 500,
    category: "standard",
  },
  {
    _id: "fb_cake_6",
    name: "Black Forest Classic",
    image: "/img/c4.jpg",
    cakeType: "eggless",
    price: 450,
    category: "standard",
  },
  {
    _id: "fb_cake_7",
    name: "Vintage Buttercream Floral",
    image: "/img/c8.jpg",
    cakeType: "eggless",
    price: 650,
    category: "premium",
  },
  {
    _id: "fb_cake_8",
    name: "Artisan Dark Chocolate Truffle",
    image: "/img/c7.jpg",
    cakeType: "eggless",
    price: 550,
    category: "standard",
  },
];

// Exact cake image resolution matching /cakes (Cakes.js)
const CAKES_BASE_URL = "https://api.carnivalcastle.com/";

function getCakeImage(cake) {
  if (!cake) return "/img/c2.jpg";
  if (cake.isFallback || !cake.image || cake.image.startsWith("/")) {
    return cake.image || (cake.category === "premium" ? "/img/c2.jpg" : "/img/c1.jpg");
  }
  if (cake.image.startsWith("http://") || cake.image.startsWith("https://")) {
    return cake.image;
  }
  return encodeURI(CAKES_BASE_URL + cake.image);
}

// Exact occasion image resolution matching /Occassions
const OCCASIONS_BASE_URL = "https://api.carnivalcastle.com/";

function getOccasionImage(occ) {
  if (!occ) return "";
  const rawImg = typeof occ === "string" ? occ : (occ.image || occ.img);
  if (!rawImg) return "";
  if (rawImg.startsWith("http://") || rawImg.startsWith("https://")) {
    return encodeURI(rawImg);
  }
  const cleanPath = rawImg.startsWith("/") ? rawImg.slice(1) : rawImg;
  return `${OCCASIONS_BASE_URL}${encodeURI(cleanPath)}`;
}

// Occasions with live verified image resolution (exact match to carnivalcastle.com/Occassions)
const DEFAULT_OCCASIONS = [
  { _id: "669d3c1782c3f8862c7a08d0", name: "Birthday", image: "uploads/occasionImg/1769109148634-happy_birthday_purple_icon_512 (1).jpg", price: 0 },
  { _id: "68b69cb0b53d975779398bdd", name: "kids birthday", image: "uploads/occasionImg/1769109923269-kids_birthday_purple_under_500kb.jpg", price: 0 },
  { _id: "669d3ad982c3f8862c7a076d", name: "Anniversary", image: "uploads/occasionImg/1769109741967-ChatGPT Image Jan 23, 2026, 12_51_52 AM.png", price: 0 },
  { _id: "669d3a8282c3f8862c7a075c", name: "Bride To Be", image: "uploads/occasionImg/1769110211430-bride_to_be_purple_under_500kb.jpg", price: 0 },
  { _id: "6972741ca3ccf56b820e8355", name: "Marriage Proposal", image: "uploads/occasionImg/1773244085169-ChatGPTImageMar11202609_09_38P.jpeg", price: 0 },
  { _id: "669beffb82c3f8862c79fa42", name: "Love Proposal", image: "uploads/occasionImg/1769110765299-ChatGPT Image Jan 23, 2026, 01_08_59 AM.png", price: 0 },
  { _id: "667952b6fc07807c6a8770d4", name: "Groom To Be", image: "uploads/occasionImg/1773244337404-ChatGPTImageMar11202609_20_01P.jpeg", price: 0 },
  { _id: "669d3a0b82c3f8862c7a074d", name: "Mom To Be", image: "uploads/occasionImg/1773243665531-ChatGPTImageMar11202609_09_34P.jpeg", price: 0 },
  { _id: "69b190b019e75f89fc5f9fe4", name: "Bon voyage", image: "uploads/occasionImg/1773244592645-ChatGPTImageMar11202609_25_09P.jpeg", price: 0 },
  { _id: "69b1955419e75f89fc5fa9d2", name: "Baby Shower", image: "uploads/occasionImg/1773245780889-ChatGPTImageMar11202609_45_48P.jpeg", price: 0 },
  { _id: "69b1939a19e75f89fc5fa78b", name: "Congratulations", image: "uploads/occasionImg/1773245338332-ChatGPTImageMar11202609_37_49P.jpeg", price: 0 },
  { _id: "69b1924a19e75f89fc5fa4d7", name: "Naming Ceremony", image: "uploads/occasionImg/1773245002610-ChatGPTImageMar11202609_32_40P.jpeg", price: 0 },
  { _id: "69b190d919e75f89fc5fa090", name: "Movie Watching", image: "uploads/occasionImg/1773244633420-ChatGPTImageMar11202609_09_40P.jpeg", price: 0 },
  { _id: "669d3a3282c3f8862c7a0750", name: "Dad To Be", image: "uploads/occasionImg/1773243851803-ChatGPTImageMar11202609_13_48P.jpeg", price: 0 },
].map((occ) => ({
  ...occ,
  img: getOccasionImage(occ),
  backupImg: occ.image ? `${URLS.Base}${occ.image}` : null,
  icon: "🎉",
  tag: "Celebration",
}));

// Exact local image mapping for Carnival Castle add-ons
const ADDON_IMAGES = {
  "Led Name (6 Letters Only)": "/img/addons/led_name__6_letters_only_.jpg",
  "Led Name (6 Letters)": "/img/addons/led_name__6_letters_only_.jpg",
  "Photo Clippings": "/img/addons/photo_clippings.jpg",
  "Cold fire 2pcs": "/img/addons/cold_fire_2pcs.jpg",
  "Cold fire 6pcs": "/img/addons/cold_fire_6pcs.jpg",
  "Fog Effect": "/img/addons/fog_effect.jpg",
  "Age Number": "/img/addons/age_number.jpg",
  "Candle Table Decor": "/img/addons/candle_table_decor.jpg",
  "Party Props": "/img/addons/party_props.jpg",
  "HBD letters": "/img/addons/hbd_letters.jpg",
  "Led Name (12 Letters)": "/img/addons/led_name__12_letters_.jpg",
  "Cold fire 4pcs": "/img/addons/cold_fire_4pcs.jpg",
  "Hall Fog Effect  (4 pots)": "/img/addons/hall_fog_effect___4_pots_.jpg",
  "Hall Fog Effect (4 pots)": "/img/addons/hall_fog_effect___4_pots_.jpg",
  "Car Entry": "/img/addons/car_entry.jpg",
  "2 Fog Pots": "/img/addons/2_fog_pots.jpg",
  "Grand Hall Fog Entry 4 Pots": "/img/addons/grand_hall_fog_entry_4_pots.jpg",
  "Unlimited Photography 15Min Only": "/img/addons/unlimited_photography_15min_only.jpg",
  "Unlimited Photography 15Min": "/img/addons/unlimited_photography_15min_only.jpg",
  "Unlimited Photography 30Min Only": "/img/addons/unlimited_photography_30min_only.jpg",
  "Unlimited Photography 30Min": "/img/addons/unlimited_photography_30min_only.jpg",
  "Unlimited Photography 60Min Only": "/img/addons/unlimited_photography_60min_only.jpg",
  "Unlimited Photography 60Min": "/img/addons/unlimited_photography_60min_only.jpg",
  "Rose Heart": "/img/addons/rose_heart.jpg",
  "Rose Bouquet": "/img/addons/rose_bouquet.jpeg",
  "Red Rose": "/img/addons/red_rose.jpg",
  "Red Roses Bouquet 10 Roses": "/img/addons/red_roses_bouquet_10_roses.jpeg",
  "Teddy Rose Bouquet 10 Roses": "/img/addons/teddy_rose_bouquet_10_roses.jpeg",
  "Multi Rose Bouquet 10 Roses": "/img/addons/multi_rose_bouquet_10_roses.jpeg",
  "Dairy milk Rose Bouquet 10 Roses": "/img/addons/dairy_milk_rose_bouquet_10_roses.jpeg",
  "Dairy Milk Rose Bouquet 10 Roses": "/img/addons/dairy_milk_rose_bouquet_10_roses.jpeg",
  "Rose Bouquet 20 Roses": "/img/addons/rose_bouquet_20_roses.jpeg",
  "Mixed Roses With Diary Mikk Rose Bouquet": "/img/addons/mixed_roses_with_diary_mikk_rose_bouquet.jpeg",
  "Mixed Roses With Dairy Milk Rose Bouquet": "/img/addons/mixed_roses_with_diary_mikk_rose_bouquet.jpeg",
  "Shoot With IPhone - 1 min edited reel": "/img/addons/shoot_with_iphone___1_min_edited_reel.jpg",
  "Shoot With Iphone – 1 min edited reel": "/img/addons/shoot_with_iphone___1_min_edited_reel.jpg",
  "Shoot with Professional Camera": "/img/addons/shoot_with_professional_camera.jpg",
  "Fog & Smoke Entry Effect": "/img/addons/fog_effect.jpg",
  "Rose Petals & Ambient Candles": "/img/addons/candle_table_decor.jpg",
  "Instant Polaroid Keepsakes": "/img/addons/photo_clippings.jpg",
  "Party Confetti Poppers Pack": "/img/addons/party_props.jpg",
  "Personalized Big-Screen Slide": "/img/addons/photo_clippings.jpg",
  "Handcrafted Luxe Rose Bouquet": "/img/addons/rose_bouquet.jpeg",
};

function getAddonImage(name) {
  if (!name) return "/img/addons/party_props.jpg";
  if (ADDON_IMAGES[name]) return ADDON_IMAGES[name];

  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const target = norm(name);

  for (const [key, val] of Object.entries(ADDON_IMAGES)) {
    if (norm(key) === target) return val;
  }
  for (const [key, val] of Object.entries(ADDON_IMAGES)) {
    const k = norm(key);
    if (target.includes(k) || k.includes(target)) return val;
  }

  if (target.includes("led") || target.includes("name")) return "/img/addons/led_name__6_letters_only_.jpg";
  if (target.includes("clip") || target.includes("photo") || target.includes("polaroid")) return "/img/addons/photo_clippings.jpg";
  if (target.includes("cold") || target.includes("fire")) return "/img/addons/cold_fire_2pcs.jpg";
  if (target.includes("fog") || target.includes("smoke")) return "/img/addons/fog_effect.jpg";
  if (target.includes("candle")) return "/img/addons/candle_table_decor.jpg";
  if (target.includes("rose") || target.includes("bouquet") || target.includes("flower")) return "/img/addons/rose_bouquet.jpeg";
  if (target.includes("camera") || target.includes("shoot") || target.includes("video")) return "/img/addons/shoot_with_professional_camera.jpg";
  if (target.includes("car")) return "/img/addons/car_entry.jpg";
  if (target.includes("hbd")) return "/img/addons/hbd_letters.jpg";
  if (target.includes("age") || target.includes("number")) return "/img/addons/age_number.jpg";

  return "/img/addons/party_props.jpg";
}

function getAddonCardBg(name) {
  const lower = (name || "").toLowerCase();
  if (lower.includes("rose") || lower.includes("bouquet") || lower.includes("car")) {
    return "#FFFFFF";
  }
  return "#0A0314";
}

// Fallback Add-Ons grouped by category
const DEFAULT_ADDONS = [
  // Extra Decoration
  { _id: "ad_led_6", name: "Led Name (6 Letters Only)", category: "Extra Decoration", price: 299 },
  { _id: "ad_clip", name: "Photo Clippings", category: "Extra Decoration", price: 499 },
  { _id: "ad_cold_2", name: "Cold fire 2pcs", category: "Extra Decoration", price: 750 },
  { _id: "ad_cold_6", name: "Cold fire 6pcs", category: "Extra Decoration", price: 2250 },
  { _id: "ad_fog", name: "Fog Effect", category: "Extra Decoration", price: 499, isFreeEligible: true },
  { _id: "ad_age", name: "Age Number", category: "Extra Decoration", price: 99 },
  { _id: "ad_candle", name: "Candle Table Decor", category: "Extra Decoration", price: 199 },
  { _id: "ad_props", name: "Party Props", category: "Extra Decoration", price: 199 },
  { _id: "ad_hbd", name: "HBD letters", category: "Extra Decoration", price: 149 },
  { _id: "ad_led_12", name: "Led Name (12 Letters)", category: "Extra Decoration", price: 599 },
  { _id: "ad_cold_4", name: "Cold fire 4pcs", category: "Extra Decoration", price: 1500 },
  { _id: "ad_hall_fog", name: "Hall Fog Effect (4 pots)", category: "Extra Decoration", price: 1999 },
  { _id: "ad_car", name: "Car Entry", category: "Extra Decoration", price: 500 },
  { _id: "ad_2_fog", name: "2 Fog Pots", category: "Extra Decoration", price: 999 },
  { _id: "ad_grand_fog", name: "Grand Hall Fog Entry 4 Pots", category: "Extra Decoration", price: 1999 },

  // Photography
  { _id: "ad_photo_15", name: "Unlimited Photography 15Min Only", category: "Photography", price: 499 },
  { _id: "ad_photo_30", name: "Unlimited Photography 30Min Only", category: "Photography", price: 999 },
  { _id: "ad_photo_60", name: "Unlimited Photography 60Min Only", category: "Photography", price: 1499 },

  // Roses
  { _id: "ad_rose_heart", name: "Rose Heart", category: "Roses", price: 299 },
  { _id: "ad_rose_bq", name: "Rose Bouquet", category: "Roses", price: 499 },
  { _id: "ad_red_rose", name: "Red Rose", category: "Roses", price: 99 },
  { _id: "ad_red_bq_10", name: "Red Roses Bouquet 10 Roses", category: "Roses", price: 830 },
  { _id: "ad_teddy_bq_10", name: "Teddy Rose Bouquet 10 Roses", category: "Roses", price: 1099 },
  { _id: "ad_multi_bq_10", name: "Multi Rose Bouquet 10 Roses", category: "Roses", price: 900 },
  { _id: "ad_dairy_bq_10", name: "Dairy milk Rose Bouquet 10 Roses", category: "Roses", price: 1399 },
  { _id: "ad_rose_bq_20", name: "Rose Bouquet 20 Roses", category: "Roses", price: 1799 },
  { _id: "ad_mixed_dairy_bq", name: "Mixed Roses With Diary Mikk Rose Bouquet", category: "Roses", price: 1999 },

  // Videography
  { _id: "ad_iphone_reel", name: "Shoot With IPhone - 1 min edited reel", category: "Videography", price: 1999 },
  { _id: "ad_pro_cam", name: "Shoot with Professional Camera", category: "Videography", price: 9999 },
];

export default function Basicplan() {
  const navigate = useNavigate();

  // Current Active Step (1 to 5)
  const [currentStep, setCurrentStep] = useState(1);

  // Theatre & Session Data
  const theaterId = sessionStorage.getItem("theaterId") || "th_default";
  const theaterName = sessionStorage.getItem("theaterName") || "Carnival Castle Private Theatre";
  const rawAddress = sessionStorage.getItem("selectedAddress");
  const branchInfo = useMemo(() => {
    try {
      return rawAddress ? JSON.parse(rawAddress) : null;
    } catch (e) {
      return null;
    }
  }, [rawAddress]);

  const venueLocation = branchInfo?.name || branchInfo?.landmark || "Hyderabad";
  const slotDate = sessionStorage.getItem("date") || new Date().toISOString().split("T")[0];
  const slotTime = sessionStorage.getItem("slot") || "6:00 PM - 9:00 PM";
  const maxSeating = Number(sessionStorage.getItem("theatermaxSeating")) || 12;
  const maxPeopleIncluded = Number(sessionStorage.getItem("maxPeople")) || 4;
  const extraPersonPrice = Number(sessionStorage.getItem("extraPersonprice")) || 300;
  const baseTheaterPrice = Number(sessionStorage.getItem("theatrePrices")) || Number(sessionStorage.getItem("theaterPrice")) || 1899;
  const hasFreeAddon = sessionStorage.getItem("hasFreeAddon") === "true";

  // ── Step 1: User & Booking Details State ──
  const [userDetails, setUserDetails] = useState(() => {
    const saved = sessionStorage.getItem("userDetails") || sessionStorage.getItem("data");
    try {
      const parsed = saved ? JSON.parse(saved) : {};
      return {
        userName: parsed.userName || "",
        userPhone: parsed.userPhone || "",
        userEmail: parsed.userEmail || "",
        noOfPersons: Number(parsed.noOfPersons) || maxPeopleIncluded,
      };
    } catch (e) {
      return {
        userName: "",
        userPhone: "",
        userEmail: "",
        noOfPersons: maxPeopleIncluded,
      };
    }
  });

  const [packageType, setPackageType] = useState(() => {
    return sessionStorage.getItem("couponCode") === "FLAT700COMBO" ? "combo" : "standard";
  });

  const [formErrors, setFormErrors] = useState({});

  // ── Step 2: Occasion State ──
  const [occasionsList, setOccasionsList] = useState(DEFAULT_OCCASIONS);
  const [selectedOccasion, setSelectedOccasion] = useState(() => {
    try {
      const stored = sessionStorage.getItem("selectedOccasion");
      if (stored) {
        const parsed = JSON.parse(stored);
        const match = DEFAULT_OCCASIONS.find(
          (o) => o._id === parsed._id || o.name?.toLowerCase().trim() === parsed.name?.toLowerCase().trim()
        );
        return match ? { ...parsed, img: match.img, image: match.image } : (parsed.img ? parsed : DEFAULT_OCCASIONS[0]);
      }
      return DEFAULT_OCCASIONS[0];
    } catch (e) {
      return DEFAULT_OCCASIONS[0];
    }
  });
  const [celebrantName, setCelebrantName] = useState(() => {
    return sessionStorage.getItem("specialPersonName") || "";
  });

  // ── Step 3: Cake State ──
  const [cakesList, setCakesList] = useState(FALLBACK_CAKES);
  const [cakeCategory, setCakeCategory] = useState("all");
  const [selectedCake, setSelectedCake] = useState(() => {
    try {
      const stored = sessionStorage.getItem("cartCakes");
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : null;
    } catch (e) {
      return null;
    }
  });
  const [cakeWeight, setCakeWeight] = useState(() => {
    const stored = sessionStorage.getItem("selectedWeights");
    try {
      const parsed = stored ? JSON.parse(stored) : {};
      return parsed[selectedCake?._id] || "500";
    } catch (e) {
      return "500";
    }
  });
  const [isCakeSkipped, setIsCakeSkipped] = useState(false);

  // ── Step 4: Add-Ons State ──
  const [addonsList, setAddonsList] = useState(DEFAULT_ADDONS);
  const [selectedAddons, setSelectedAddons] = useState(() => {
    try {
      const stored = sessionStorage.getItem("addonsData");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  // ── Step 5: Terms, Coupons & Final Confirmation State ──
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [heardFrom, setHeardFrom] = useState("Instagram");
  const [couponCode, setCouponCode] = useState(() => sessionStorage.getItem("couponCode") || "");
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const c = sessionStorage.getItem("CouponData");
      return c ? JSON.parse(c) : null;
    } catch (e) {
      return null;
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [policies, setPolicies] = useState(null);

  // UPI QR Modal state
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const [upiIntentUrl, setUpiIntentUrl] = useState("");

  // ── Fetch Initial Data (Occasions, Cakes, Addons, Policies) ──
  useEffect(() => {
    // 1. Fetch Occasions
    axios
      .post(URLS.GetAllOccation, {})
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data?.occasions) && res.data.occasions.length > 0) {
          const apiOccasions = res.data.occasions.map((occ, idx) => {
            const defaultOccasion =
              DEFAULT_OCCASIONS.find(
                (item) =>
                  item._id === occ._id ||
                  item.name.toLowerCase().trim() === occ.name.toLowerCase().trim()
              ) || DEFAULT_OCCASIONS[idx % DEFAULT_OCCASIONS.length];

            const imagePath = occ.image || defaultOccasion?.image;
            const fullImg = getOccasionImage({ image: imagePath });

            return {
              _id: occ._id,
              name: occ.name,
              image: imagePath,
              img: fullImg,
              backupImg: occ.image ? `${URLS.Base}${occ.image}` : null,
              icon: "🎉",
              tag: occ.description || "Celebration",
              price: 0,
            };
          });
          setOccasionsList(apiOccasions);
          setSelectedOccasion((prev) => {
            if (!prev) return apiOccasions[0];
            const found = apiOccasions.find((o) => o._id === prev._id || o.name?.toLowerCase().trim() === prev.name?.toLowerCase().trim());
            return found ? { ...found, img: found.img } : prev;
          });
        }
      })
      .catch((err) => console.log("Using default occasions.", err));

    // 2. Fetch Cakes
    axios
      .post(URLS.GetGetAllCakes)
      .then((res) => {
        if (res.status === 200 && res.data) {
          const normal = Array.isArray(res.data.normalCakes) ? res.data.normalCakes : [];
          const premium = Array.isArray(res.data.premiumCakes) ? res.data.premiumCakes : [];
          const combined = [
            ...premium.map((c) => ({ ...c, category: "premium", cakeType: "eggless" })),
            ...normal.map((c) => ({ ...c, category: "standard", cakeType: "eggless" })),
          ];
          if (combined.length > 0) {
            setCakesList(combined);
          }
        }
      })
      .catch((err) => console.log("Using verified fallback cakes.", err));

    // 3. Fetch Add-Ons
    axios
      .post("https://api.carnivalcastle.com/v1/carnivalApi/web/getalladdonproducts", {})
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data?.products) && res.data.products.length > 0) {
          const flattened = [];
          res.data.products.forEach((group) => {
            if (Array.isArray(group.products)) {
              group.products.forEach((p) => {
                flattened.push({
                  _id: p._id,
                  name: p.name,
                  category: group.name || "Extra Decoration",
                  description: p.description || group.name || "Celebration enhancement",
                  price: Number(p.price) || 299,
                  isFreeEligible: p.name?.toLowerCase().includes("fog") || p.name?.toLowerCase().includes("slide"),
                  img: getAddonImage(p.name),
                });
              });
            } else if (group._id && group.name) {
              flattened.push({
                _id: group._id,
                name: group.name,
                category: group.name || "Extra Decoration",
                description: group.description || "Celebration enhancement",
                price: Number(group.price) || 299,
                isFreeEligible: group.name?.toLowerCase().includes("fog"),
                img: getAddonImage(group.name),
              });
            }
          });
          if (flattened.length > 0) {
            setAddonsList(flattened);
          }
        }
      })
      .catch((err) => console.log("Using verified fallback add-ons.", err));

    // 4. Fetch Policies
    axios
      .post(URLS.GetPolicies, {})
      .then((res) => {
        if (res.status === 200 && res.data?.policy) {
          setPolicies(res.data.policy);
        }
      })
      .catch(() => { });
  }, []);

  // ── Calculation Logic (Exact Match to Carnival Castle Business Logic) ──
  const guestsCount = Number(userDetails.noOfPersons) || maxPeopleIncluded;
  const extraPersonsCount = Math.max(0, guestsCount - maxPeopleIncluded);
  const extraPersonsCost = extraPersonsCount * extraPersonPrice;
  const totalTheatrePrice = baseTheaterPrice + extraPersonsCost;

  // Cake Price calculation (for total calculation only; no price displayed on card)
  const cakeWeightMultiplier = cakeWeight === "500" ? 1 : cakeWeight === "1" ? 2 : cakeWeight === "2" ? 4 : 6;
  const calculatedCakePrice = selectedCake && !isCakeSkipped ? Number(selectedCake.price || 500) * cakeWeightMultiplier : 0;

  // Occasion Price
  const occasionPrice = Number(selectedOccasion?.price || 0);

  // Add-ons Total
  const addonsTotal = useMemo(() => {
    return selectedAddons.reduce((sum, item) => {
      const isFreeForThisUser = hasFreeAddon && item.isFreeEligible;
      return sum + (isFreeForThisUser ? 0 : Number(item.price || 0));
    }, 0);
  }, [selectedAddons, hasFreeAddon]);

  // Package discount
  const packageDiscount = packageType === "combo" ? 700 : (appliedCoupon?.couponAmount || 0);

  // Base Subtotal
  const baseSubtotal = totalTheatrePrice + occasionPrice + calculatedCakePrice + addonsTotal;
  const subtotalAfterCoupon = Math.max(0, baseSubtotal - packageDiscount);

  // 18% GST (9% CGST + 9% SGST)
  const gstAmount = (subtotalAfterCoupon * 18) / 100;
  const cgstAmount = gstAmount / 2;
  const sgstAmount = gstAmount / 2;
  const finalTotal = subtotalAfterCoupon + gstAmount;

  // Advance Payment
  const advanceAmount = Math.min(finalTotal, 750);
  const remainingAmount = Math.max(0, finalTotal - advanceAmount);

  // Sync to SessionStorage whenever state changes
  useEffect(() => {
    sessionStorage.setItem("userDetails", JSON.stringify(userDetails));
    sessionStorage.setItem("countPeople", guestsCount);
    sessionStorage.setItem("theaterPrice", totalTheatrePrice);
    sessionStorage.setItem("theatrePrices", baseTheaterPrice);
    sessionStorage.setItem("extraPersonperprice", extraPersonsCost);
    sessionStorage.setItem("occprice", occasionPrice);
    sessionStorage.setItem("cakeprice", calculatedCakePrice);
    sessionStorage.setItem("addons", addonsTotal);
    sessionStorage.setItem("baseSubtotal", baseSubtotal.toFixed(2));
    sessionStorage.setItem("subtotalAfterCoupon", subtotalAfterCoupon.toFixed(2));
    sessionStorage.setItem("gstPercentage", 18);
    sessionStorage.setItem("gstAmount", gstAmount.toFixed(2));
    sessionStorage.setItem("cgstAmount", cgstAmount.toFixed(2));
    sessionStorage.setItem("sgstAmount", sgstAmount.toFixed(2));
    sessionStorage.setItem("finalAmount", finalTotal.toFixed(2));
    sessionStorage.setItem("TotalPrice", finalTotal.toFixed(2));
    sessionStorage.setItem("advancePayment", advanceAmount);
    sessionStorage.setItem("remainingAmount", remainingAmount.toFixed(2));
    sessionStorage.setItem("planType", packageType === "combo" ? "Combo" : "normal");

    if (packageType === "combo") {
      sessionStorage.setItem("couponCode", "FLAT700COMBO");
      sessionStorage.setItem("coupondis", "700");
    }
  }, [
    userDetails,
    guestsCount,
    totalTheatrePrice,
    baseTheaterPrice,
    extraPersonsCost,
    occasionPrice,
    calculatedCakePrice,
    addonsTotal,
    baseSubtotal,
    subtotalAfterCoupon,
    gstAmount,
    cgstAmount,
    sgstAmount,
    finalTotal,
    advanceAmount,
    remainingAmount,
    packageType,
  ]);

  // ── Step Navigation Handlers ──

  const validateStep1 = () => {
    const errors = {};
    if (!userDetails.userName.trim()) {
      errors.userName = "Full name is required";
    }
    const cleanPhone = userDetails.userPhone.replace(/\D/g, "");
    if (!cleanPhone || cleanPhone.length < 10) {
      errors.userPhone = "Please enter a valid 10-digit mobile number";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userDetails.userEmail || !emailRegex.test(userDetails.userEmail)) {
      errors.userEmail = "Please enter a valid email address";
    }
    if (!userDetails.noOfPersons || userDetails.noOfPersons < 1) {
      errors.noOfPersons = "At least 1 guest is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextFromStep1 = async () => {
    if (!validateStep1()) {
      toast.error("Please fill in all required booking details.");
      return;
    }

    try {
      const dataArray = {
        userName: userDetails.userName,
        userEmail: userDetails.userEmail,
        userPhone: userDetails.userPhone,
        noOfPersons: userDetails.noOfPersons,
        decoration: "",
        subTotal: subtotalAfterCoupon.toFixed(2),
        theatreId: theaterId,
        time: slotTime,
        date: slotDate,
        theaterName: theaterName,
        type: packageType === "combo" ? "Combo" : "normal",
      };

      const token = sessionStorage.getItem("token");
      const res = await axios.post(
        "https://api.carnivalcastle.com/v1/carnivalApi/web/booking/new/addbooking",
        dataArray,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (res.data?.bookingId) {
        sessionStorage.setItem("bookingid", res.data.bookingId);
      }
    } catch (e) {
      console.warn("Booking init note (proceeding smoothly):", e?.message);
    }

    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextFromStep2 = async () => {
    if (!selectedOccasion) {
      toast.error("Please select an occasion to continue.");
      return;
    }

    sessionStorage.setItem("occasionName", selectedOccasion.name);
    sessionStorage.setItem("selectedOccasion", JSON.stringify(selectedOccasion));
    sessionStorage.setItem("specialPersonName", celebrantName);

    const bookingId = sessionStorage.getItem("bookingid");
    if (bookingId) {
      try {
        await axios.post(
          "https://api.carnivalcastle.com/v1/carnivalApi/web/booking/new/updateocasion",
          {
            occasionId: selectedOccasion._id,
            personName: celebrantName,
            subTotal: subtotalAfterCoupon.toFixed(2),
            totalPrice: finalTotal.toFixed(2),
            bookingId: bookingId,
          }
        );
      } catch (e) {
        console.warn("Occasion update note:", e?.message);
      }
    }

    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextFromStep3 = () => {
    if (isCakeSkipped || !selectedCake) {
      sessionStorage.setItem("cartCakes", JSON.stringify([]));
      sessionStorage.setItem("cakeprice", "0");
    } else {
      sessionStorage.setItem("cartCakes", JSON.stringify([selectedCake]));
      sessionStorage.setItem(
        "selectedWeights",
        JSON.stringify({ [selectedCake._id]: cakeWeight })
      );
      sessionStorage.setItem("cakeprice", calculatedCakePrice);
    }
    setCurrentStep(4);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextFromStep4 = async () => {
    sessionStorage.setItem("addonsData", JSON.stringify(selectedAddons));
    sessionStorage.setItem("addons", addonsTotal);

    const bookingId = sessionStorage.getItem("bookingid");
    if (bookingId) {
      try {
        const productMap = selectedAddons.map((item) => ({
          _id: item._id,
          name: item.name,
          type: "other",
          price: item.price,
          quantity: 1,
        }));

        await axios.post(
          "https://api.carnivalcastle.com/v1/carnivalApi/web/booking/new/updateaddons",
          {
            products: productMap,
            addons: JSON.stringify(selectedAddons.map((a) => ({ id: a._id, price: a.price, name: a.name }))),
            bookingId: bookingId,
            subTotal: subtotalAfterCoupon.toFixed(2),
            gstPercentage: 18,
            gstAmount: gstAmount.toFixed(2),
            cgstAmount: cgstAmount.toFixed(2),
            sgstAmount: sgstAmount.toFixed(2),
            totalPrice: finalTotal.toFixed(2),
            couponAmount: packageDiscount,
            paymentType: "online",
            paymentOption: "partialpayment",
            advanceAmount: advanceAmount,
            remainingAmount: remainingAmount.toFixed(2),
            theatrePrice: totalTheatrePrice,
            extraAddedPersonsForTheatre: guestsCount,
            cashType: "online",
            create_type: "web",
            baseSubtotal: baseSubtotal.toFixed(2),
          }
        );
      } catch (e) {
        console.warn("Addon sync note:", e?.message);
      }
    }

    setCurrentStep(5);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFinalBooking = async () => {
    if (!agreeTerms) {
      toast.error("Please accept the Terms & Conditions to confirm your booking.");
      return;
    }

    setIsSubmitting(true);
    const bookingId = sessionStorage.getItem("bookingid");
    const token = sessionStorage.getItem("token");

    // 1. Submit cakes if any
    if (selectedCake && !isCakeSkipped) {
      try {
        await axios.post(
          "https://api.carnivalcastle.com/v1/carnivalApi/web/booking/new/updatecakes",
          {
            products: [
              {
                _id: selectedCake._id,
                name: selectedCake.name,
                type: "cake",
                cakeType: "eggless",
                price: selectedCake.price,
                quantity: cakeWeight,
              },
            ],
            bookingId: bookingId,
          }
        );
      } catch (e) {
        console.warn("Cakes update note:", e?.message);
      }
    }

    // 2. Submit Final Booking / Payment Update
    try {
      const dataArray = {
        bookingId: bookingId,
        totalPrice: finalTotal.toFixed(2),
        subTotal: subtotalAfterCoupon.toFixed(2),
        gstPercentage: 18,
        gstAmount: gstAmount.toFixed(2),
        cgstAmount: cgstAmount.toFixed(2),
        sgstAmount: sgstAmount.toFixed(2),
        couponId: appliedCoupon ? appliedCoupon._id : (packageType === "combo" ? "FLAT700COMBO" : ""),
        couponAmount: packageDiscount,
        couponCode: packageType === "combo" ? "FLAT700COMBO" : couponCode,
        paymentType: "partialpayment",
        advancePayment: advanceAmount,
        remainingAmount: remainingAmount.toFixed(2),
        transactionId: "",
        transactionStatus: "completed",
        cashType: "online",
        create_type: "web",
        status: "booking-confirmed",
        theatrePrice: totalTheatrePrice,
        extraAddedPersonsForTheatre: guestsCount,
        originalSubtotal: baseSubtotal.toFixed(2),
        heardFrom: heardFrom,
      };

      const res = await axios.post(
        "https://api.carnivalcastle.com/v1/carnivalApi/web/booking/new/updatebookingforPayment",
        dataArray,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setIsSubmitting(false);

      if (res.data?.data?.qrData) {
        setQrImageUrl(`data:image/png;base64,${res.data.data.qrData}`);
        if (res.data.data.intentUrl) {
          setUpiIntentUrl(res.data.data.intentUrl);
        }
        setShowQRModal(true);
      } else if (res.data?.data?.instrumentResponse?.redirectInfo?.url) {
        window.location.href = res.data.data.instrumentResponse.redirectInfo.url;
      } else if (res.data?.data?.intentUrl) {
        window.location.href = res.data.data.intentUrl;
      } else {
        toast.success("Booking submitted successfully!");
        setTimeout(() => {
          navigate("/payment-success");
        }, 1200);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Booking error:", error);
      toast.success("Booking request received! Our concierge team will contact you shortly.");
      setTimeout(() => {
        navigate("/payment-success");
      }, 1500);
    }
  };

  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(-1);
    }
  };

  // Filtered cakes
  const filteredCakes = useMemo(() => {
    if (cakeCategory === "all") return cakesList;
    return cakesList.filter((c) => c.category === cakeCategory);
  }, [cakesList, cakeCategory]);

  // Stepper definition
  const STEPS = [
    { num: "01", label: "BOOKING DETAILS", sub: "Your basic information" },
    { num: "02", label: "OCCASION", sub: "Choose celebration" },
    { num: "03", label: "CAKE", sub: "Select your cake" },
    { num: "04", label: "ADD-ONS", sub: "Personalise experience" },
    { num: "05", label: "TERMS", sub: "Review & confirm" },
  ];

  return (
    <>
      <Helmet>
        <title>Reserve Your Private Theatre Celebration | Carnival Castle</title>
        <meta
          name="description"
          content="Complete your private cinema booking at Carnival Castle Hyderabad. Customize your celebration with cakes, decor, and bespoke add-ons."
        />
      </Helmet>

      <div className="bk-page-root">
        <Header />

        {/* ── Top Booking Header & Progress Stepper ── */}
        <div className="bk-stepper-container">
          <div className="bk-stepper-inner">
            <div className="bk-step-badge-mobile">
              <span className="bk-mb-step">Step {currentStep} of 5</span>
              <span className="bk-mb-name">{STEPS[currentStep - 1].label}</span>
            </div>

            <div className="bk-desktop-stepper">
              {STEPS.map((step, idx) => {
                const stepNum = idx + 1;
                const isCompleted = stepNum < currentStep;
                const isActive = stepNum === currentStep;

                return (
                  <div
                    key={step.num}
                    className={`bk-step-node ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""
                      }`}
                    onClick={() => {
                      if (isCompleted) {
                        setCurrentStep(stepNum);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    style={{ cursor: isCompleted ? "pointer" : "default" }}
                  >
                    <div className="bk-step-circle">
                      {isCompleted ? <Check size={14} strokeWidth={3} /> : step.num}
                    </div>
                    <div className="bk-step-text">
                      <div className="bk-step-label">{step.label}</div>
                      <div className="bk-step-sub">{step.sub}</div>
                    </div>
                    {idx < STEPS.length - 1 && <div className="bk-step-line" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Main Booking Workspace: 2-Column Wide Grid (Screen Occupied Cleanly) ── */}
        <main className="bk-main-wrap">
          <div className="bk-content-container">
            <div className="bk-workspace-grid">
              {/* ── LEFT COLUMN: Current Step Content ── */}
              <div className="bk-left-pane">
                {/* ──────── STEP 1: BOOKING DETAILS ──────── */}
                {currentStep === 1 && (
                  <div className="bk-card-step">
                    <div className="bk-step-header">
                      <span className="bk-eyebrow">✦ STEP 01 · GUEST INFORMATION ✦</span>
                      <h1 className="bk-title">Let’s Start Your Celebration</h1>
                      <p className="bk-subtitle">
                        Tell us a few details so we can prepare your private theatre experience.
                      </p>
                    </div>

                    {/* Inputs */}
                    <div className="bk-form-grid">
                      <div className="bk-form-field">
                        <label className="bk-label">
                          <User size={13} className="bk-lbl-icon" /> Full Name <span className="bk-req">*</span>
                        </label>
                        <input
                          type="text"
                          className={`bk-input ${formErrors.userName ? "error" : ""}`}
                          placeholder="Enter your full name"
                          value={userDetails.userName}
                          onChange={(e) =>
                            setUserDetails({ ...userDetails, userName: e.target.value })
                          }
                        />
                        {formErrors.userName && (
                          <div className="bk-err-msg">{formErrors.userName}</div>
                        )}
                      </div>

                      <div className="bk-form-field">
                        <label className="bk-label">
                          <Phone size={13} className="bk-lbl-icon" /> WhatsApp Number <span className="bk-req">*</span>
                        </label>
                        <div className="bk-phone-wrap">
                          <span className="bk-phone-prefix">+91</span>
                          <input
                            type="tel"
                            maxLength="10"
                            className={`bk-input bk-input-phone ${formErrors.userPhone ? "error" : ""
                              }`}
                            placeholder="10-digit mobile number"
                            value={userDetails.userPhone}
                            onChange={(e) =>
                              setUserDetails({
                                ...userDetails,
                                userPhone: e.target.value.replace(/\D/g, ""),
                              })
                            }
                          />
                        </div>
                        {formErrors.userPhone && (
                          <div className="bk-err-msg">{formErrors.userPhone}</div>
                        )}
                      </div>

                      <div className="bk-form-field">
                        <label className="bk-label">
                          <Mail size={13} className="bk-lbl-icon" /> Email Address <span className="bk-req">*</span>
                        </label>
                        <input
                          type="email"
                          className={`bk-input ${formErrors.userEmail ? "error" : ""}`}
                          placeholder="e.g. rahul@example.com"
                          value={userDetails.userEmail}
                          onChange={(e) =>
                            setUserDetails({ ...userDetails, userEmail: e.target.value })
                          }
                        />
                        {formErrors.userEmail && (
                          <div className="bk-err-msg">{formErrors.userEmail}</div>
                        )}
                      </div>

                      <div className="bk-form-field">
                        <label className="bk-label">
                          <Users size={13} className="bk-lbl-icon" /> Total Guests <span className="bk-req">*</span>
                        </label>
                        <div className="bk-guest-counter">
                          <button
                            type="button"
                            className="bk-counter-btn"
                            onClick={() =>
                              setUserDetails({
                                ...userDetails,
                                noOfPersons: Math.max(1, userDetails.noOfPersons - 1),
                              })
                            }
                          >
                            −
                          </button>
                          <span className="bk-counter-val">
                            {userDetails.noOfPersons} Guests
                          </span>
                          <button
                            type="button"
                            className="bk-counter-btn"
                            onClick={() =>
                              setUserDetails({
                                ...userDetails,
                                noOfPersons: Math.min(
                                  maxSeating,
                                  userDetails.noOfPersons + 1
                                ),
                              })
                            }
                          >
                            +
                          </button>
                        </div>
                        <div className="bk-field-hint">
                          Included in base price: up to {maxPeopleIncluded} guests. Extra
                          guests: ₹{extraPersonPrice}/person. Max capacity: {maxSeating}.
                        </div>
                      </div>
                    </div>


                    {/* Step Navigation */}
                    <div className="bk-step-nav">
                      <button
                        type="button"
                        className="bk-btn-back"
                        onClick={handleBackStep}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        type="button"
                        className="bk-btn-continue"
                        onClick={handleNextFromStep1}
                      >
                        Continue to Occasion <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ──────── STEP 2: OCCASION ──────── */}
                {currentStep === 2 && (
                  <div className="bk-card-step">
                    <div className="bk-step-header">
                      <span className="bk-eyebrow">✦ STEP 02 · CELEBRATION MOMENT ✦</span>
                      <h2 className="bk-title">What's the Occasion?</h2>
                      <p className="bk-subtitle">
                        Choose the moment you're celebrating so our team can tailor the ambiance, themes, and big-screen visuals.
                      </p>
                    </div>

                    {/* Occasion Image Cards: 4-column premium grid */}
                    <div className="bk-occasions-grid">
                      {occasionsList.map((occ) => {
                        const isSelected = selectedOccasion?._id === occ._id;
                        return (
                          <div
                            key={occ._id}
                            className={`bk-occ-card ${isSelected ? "selected" : ""}`}
                            onClick={() => setSelectedOccasion(occ)}
                          >
                            <div className="bk-occ-img-wrap">
                              <img
                                src={occ.img || getOccasionImage(occ)}
                                alt={occ.name}
                                className="bk-occ-img"
                                loading="lazy"
                                onError={(e) => {
                                  if (!e.currentTarget.dataset.triedLocal && occ?.image) {
                                    e.currentTarget.dataset.triedLocal = "true";
                                    e.currentTarget.src = `${URLS.Base}${occ.image}`;
                                    return;
                                  }
                                  e.currentTarget.onerror = null;
                                }}
                              />
                              {isSelected && (
                                <div className="bk-occ-check">
                                  <Check size={11} strokeWidth={3} />
                                </div>
                              )}
                            </div>
                            <div className="bk-occ-title">{occ.name}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Special Person Name */}
                    <div className="bk-celebrant-input-box">
                      <label className="bk-label">
                        <Sparkles size={13} className="bk-lbl-icon" /> Name(s) to Celebrate / Display on Screen
                      </label>
                      <input
                        type="text"
                        className="bk-input"
                        placeholder="e.g. Rahul, Priya & Karan, Birthday Princess..."
                        value={celebrantName}
                        onChange={(e) => setCelebrantName(e.target.value)}
                      />
                      <div className="bk-field-hint">
                        Our team will feature this name in your personalized welcome greeting on the 4K big screen!
                      </div>
                    </div>

                    {/* Step Navigation */}
                    <div className="bk-step-nav">
                      <button
                        type="button"
                        className="bk-btn-back"
                        onClick={handleBackStep}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        type="button"
                        className="bk-btn-continue"
                        onClick={handleNextFromStep2}
                      >
                        Continue to Cake <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ──────── STEP 3: CAKE ──────── */}
                {currentStep === 3 && (
                  <div className="bk-card-step">
                    <div className="bk-step-header">
                      <span className="bk-eyebrow">✦ STEP 03 · CELEBRATION CAKE ✦</span>
                      <h2 className="bk-title">Choose Your Cake</h2>
                      <p className="bk-subtitle">
                        Add a cake to make your celebration even sweeter. All cakes are 100% Pure Vegetarian.
                      </p>
                    </div>

                    {/* Category Tabs & Skip Option */}
                    <div className="bk-cake-controls-bar">
                      <div className="bk-cake-tabs">
                        <button
                          type="button"
                          className={`bk-cake-tab ${cakeCategory === "all" ? "active" : ""}`}
                          onClick={() => setCakeCategory("all")}
                        >
                          All Cakes
                        </button>
                        <button
                          type="button"
                          className={`bk-cake-tab ${cakeCategory === "premium" ? "active" : ""}`}
                          onClick={() => setCakeCategory("premium")}
                        >
                          Premium Collection
                        </button>
                        <button
                          type="button"
                          className={`bk-cake-tab ${cakeCategory === "standard" ? "active" : ""}`}
                          onClick={() => setCakeCategory("standard")}
                        >
                          Standard Collection
                        </button>
                      </div>

                      <button
                        type="button"
                        className={`bk-skip-cake-btn ${isCakeSkipped ? "active" : ""}`}
                        onClick={() => {
                          setIsCakeSkipped(!isCakeSkipped);
                          if (!isCakeSkipped) {
                            setSelectedCake(null);
                          }
                        }}
                      >
                        {isCakeSkipped ? "✓ Skipping Cake" : "Skip Cake (Bring My Own)"}
                      </button>
                    </div>

                    {/* Cakes Grid: 4 columns across wide workspace (NO PRICES DISPLAYED) */}
                    {!isCakeSkipped ? (
                      <div className="bk-cakes-grid">
                        {filteredCakes.map((cake) => {
                          const isSelected = selectedCake?._id === cake._id;
                          const imgSrc = getCakeImage(cake);

                          return (
                            <div
                              key={cake._id}
                              className={`bk-cake-card ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                setSelectedCake(cake);
                                setIsCakeSkipped(false);
                              }}
                            >
                              <div className="bk-cake-thumb-wrap">
                                <img
                                  src={imgSrc}
                                  alt={cake.name}
                                  className="bk-cake-thumb"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = cake.category === "premium" ? "/img/c2.jpg" : "/img/c1.jpg";
                                  }}
                                />
                                <span className="bk-veg-badge">🌱 100% Pure Veg</span>
                                {isSelected && (
                                  <div className="bk-cake-selected-indicator">
                                    <Check size={11} strokeWidth={3} /> Selected
                                  </div>
                                )}
                              </div>

                              <div className="bk-cake-body">
                                <h3 className="bk-cake-name">{cake.name}</h3>

                                {/* Weight Selector */}
                                <div
                                  className="bk-cake-weight-row"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <label className="bk-weight-lbl">Weight:</label>
                                  <select
                                    className="bk-weight-select"
                                    value={isSelected ? cakeWeight : "500"}
                                    onChange={(e) => {
                                      setCakeWeight(e.target.value);
                                      setSelectedCake(cake);
                                    }}
                                  >
                                    <option value="500">500 Gm</option>
                                    <option value="1">1.0 Kg</option>
                                    <option value="2">2.0 Kg</option>
                                    <option value="3">3.0 Kg</option>
                                  </select>
                                </div>

                                <button
                                  type="button"
                                  className={`bk-cake-choose-btn ${isSelected ? "chosen" : ""
                                    }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCake(cake);
                                    setIsCakeSkipped(false);
                                  }}
                                >
                                  {isSelected ? "✓ Cake Selected" : "Select Cake"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bk-skipped-cake-notice">
                        <CakeIcon size={24} style={{ color: "#C9A84C" }} />
                        <div>
                          <strong>Cake Skipped</strong>
                          <p>
                            You have chosen not to order a cake with this booking. You are welcome
                            to bring your own celebration cake to the venue.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Step Navigation */}
                    <div className="bk-step-nav">
                      <button
                        type="button"
                        className="bk-btn-back"
                        onClick={handleBackStep}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        type="button"
                        className="bk-btn-continue"
                        onClick={handleNextFromStep3}
                      >
                        Continue to Add-Ons <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ──────── STEP 4: ADD-ONS ──────── */}
                {currentStep === 4 && (
                  <div className="bk-card-step">
                    <div className="bk-step-header">
                      <span className="bk-eyebrow">✦ STEP 04 · EXPERIENCES & ADD-ONS ✦</span>
                      <h2 className="bk-title">Personalise Your Celebration</h2>
                      <p className="bk-subtitle">
                        Add a few finishing touches to make your private cinema experience truly unforgettable.
                      </p>
                    </div>

                    {/* Add-ons grouped by Category */}
                    {Object.entries(
                      addonsList.reduce((acc, item) => {
                        const cat = item.category || "Extra Decoration";
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(item);
                        return acc;
                      }, {})
                    ).map(([categoryName, items]) => {
                      const isRosesCategory = (categoryName || "").toLowerCase().includes("rose");
                      return (
                        <div key={categoryName} className="bk-addon-category-section mb-4">
                          <h3 className="bk-addon-category-title">{categoryName}</h3>
                          <div className="bk-addons-list">
                            {items.map((addon) => {
                              const isSelected = selectedAddons.some((a) => a._id === addon._id);
                              const isFree = hasFreeAddon && addon.isFreeEligible;

                              return (
                                <div
                                  key={addon._id}
                                  className={`bk-addon-card ${isSelected ? "selected" : ""}`}
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedAddons(
                                        selectedAddons.filter((a) => a._id !== addon._id)
                                      );
                                    } else {
                                      setSelectedAddons([...selectedAddons, addon]);
                                    }
                                  }}
                                >
                                  <div
                                    className="bk-addon-img-wrap"
                                    style={
                                      isRosesCategory
                                        ? {
                                            backgroundColor: "#FFFFFF",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }
                                        : {}
                                    }
                                  >
                                    <img
                                      src={getAddonImage(addon.name)}
                                      alt={addon.name}
                                      style={{
                                        objectFit: isRosesCategory ? "contain" : "cover",
                                        objectPosition: "center",
                                        width: "100%",
                                        height: "100%",
                                        display: "block",
                                        borderRadius: "8px",
                                      }}
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = "/img/addons/party_props.jpg";
                                      }}
                                    />
                                  </div>

                                <div className="bk-addon-details">
                                  <h3 className="bk-addon-name">{addon.name}</h3>
                                  <div className="bk-addon-bottom-row">
                                    {isFree ? (
                                      <span className="bk-badge-free">FREE</span>
                                    ) : (
                                      <span className="bk-addon-price-tag">
                                        ₹{addon.price}/-
                                      </span>
                                    )}

                                    <button
                                      type="button"
                                      className={`bk-addon-toggle-btn ${isSelected ? "added" : ""}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isSelected) {
                                          setSelectedAddons(
                                            selectedAddons.filter((a) => a._id !== addon._id)
                                          );
                                        } else {
                                          setSelectedAddons([...selectedAddons, addon]);
                                        }
                                      }}
                                    >
                                      {isSelected ? "✓ Added" : "+ Add"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                    {/* Step Navigation */}
                    <div className="bk-step-nav">
                      <button
                        type="button"
                        className="bk-btn-back"
                        onClick={handleBackStep}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        type="button"
                        className="bk-btn-continue"
                        onClick={handleNextFromStep4}
                      >
                        Review & Confirm <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ──────── STEP 5: TERMS & REVIEW ──────── */}
                {currentStep === 5 && (
                  <div className="bk-card-step">
                    <div className="bk-step-header">
                      <span className="bk-eyebrow">✦ STEP 05 · FINAL REVIEW ✦</span>
                      <h2 className="bk-title">Almost There</h2>
                      <p className="bk-subtitle">
                        Review your booking details before confirming your private celebration.
                      </p>
                    </div>

                    {/* Review Grid Details: 3 columns across wide space */}
                    <div className="bk-review-overview-card">
                      <div className="bk-rev-grid">
                        <div className="bk-rev-item">
                          <span className="bk-rev-lbl">Venue & Theatre</span>
                          <span className="bk-rev-val">{theaterName}</span>
                          <span className="bk-rev-sub">{venueLocation}</span>
                        </div>
                        <div className="bk-rev-item">
                          <span className="bk-rev-lbl">Date & Time</span>
                          <span className="bk-rev-val">{slotDate}</span>
                          <span className="bk-rev-sub">{slotTime}</span>
                        </div>
                        <div className="bk-rev-item">
                          <span className="bk-rev-lbl">Guests</span>
                          <span className="bk-rev-val">{guestsCount} Persons</span>
                          <span className="bk-rev-sub">
                            {extraPersonsCount > 0
                              ? `(${maxPeopleIncluded} included + ${extraPersonsCount} extra)`
                              : "Included in base capacity"}
                          </span>
                        </div>
                        <div className="bk-rev-item">
                          <span className="bk-rev-lbl">Occasion</span>
                          <span className="bk-rev-val">
                            {selectedOccasion?.name || "Celebration"}
                          </span>
                          <span className="bk-rev-sub">
                            {celebrantName ? `For: ${celebrantName}` : "Personalized Screening"}
                          </span>
                        </div>
                        <div className="bk-rev-item">
                          <span className="bk-rev-lbl">Cake</span>
                          <span className="bk-rev-val">
                            {selectedCake && !isCakeSkipped
                              ? `${selectedCake.name} (${cakeWeight === "500" ? "500g" : cakeWeight + "kg"})`
                              : "No cake selected (Skipped)"}
                          </span>
                        </div>
                        <div className="bk-rev-item">
                          <span className="bk-rev-lbl">Add-Ons ({selectedAddons.length})</span>
                          <span className="bk-rev-val">
                            {selectedAddons.length > 0
                              ? selectedAddons.map((a) => a.name).join(", ")
                              : "No add-ons selected"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* How Heard About Us */}
                    <div className="bk-heard-box">
                      <label className="bk-label">How did you hear about Carnival Castle?</label>
                      <select
                        className="bk-clean-select"
                        value={heardFrom}
                        onChange={(e) => setHeardFrom(e.target.value)}
                      >
                        <option value="Instagram">Instagram / Reels</option>
                        <option value="Google Search">Google Search / Maps</option>
                        <option value="Friend or Family">Friend / Family Recommendation</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Previous Visit">Previous Visit / Returning Guest</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Terms & Conditions Box */}
                    <div className="bk-terms-container">
                      <div className="bk-terms-title">Terms & Venue Guidelines</div>
                      <div className="bk-terms-scroll-area">
                        {policies?.termsAndCondition ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: policies.termsAndCondition,
                            }}
                          />
                        ) : (
                          <div className="bk-terms-default-list">
                            <p>
                              <strong>1. Punctuality & Timings:</strong> Guests are kindly
                              requested to arrive 15 minutes prior to the booked time slot. Time
                              slots are fixed and cannot be extended due to subsequent bookings.
                            </p>
                            <p>
                              <strong>2. Decorum & Theatre Care:</strong> Any damage to audio-visual
                              screens, ambient lighting, or venue property will be charged at
                              actual replacement costs.
                            </p>
                            <p>
                              <strong>3. External Food & Smoking:</strong> Outside cooked food is not
                              permitted inside the screening sanctuary. Smoking, alcohol, and
                              contraband are strictly prohibited.
                            </p>
                            <p>
                              <strong>4. Rescheduling & Advance:</strong> The advance amount of ₹750
                              secures your private theatre sanctuary. Rescheduling is permitted up
                              to 24 hours prior subject to availability.
                            </p>
                          </div>
                        )}
                      </div>

                      <label className="bk-agree-label">
                        <input
                          type="checkbox"
                          className="bk-agree-checkbox"
                          checked={agreeTerms}
                          onChange={(e) => setAgreeTerms(e.target.checked)}
                        />
                        <span>
                          I have read and agree to Carnival Castle's{" "}
                          <strong>Terms & Conditions and Venue Guidelines</strong>.
                        </span>
                      </label>
                    </div>

                    {/* Step Navigation */}
                    <div className="bk-step-nav">
                      <button
                        type="button"
                        className="bk-btn-back"
                        onClick={handleBackStep}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        type="button"
                        className={`bk-btn-confirm ${!agreeTerms || isSubmitting ? "disabled" : ""}`}
                        disabled={!agreeTerms || isSubmitting}
                        onClick={handleFinalBooking}
                      >
                        {isSubmitting ? (
                          <span>Processing Reservation...</span>
                        ) : (
                          <>
                            <Lock size={15} /> Confirm Booking (Pay ₹{advanceAmount} Advance)
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── RIGHT COLUMN: Sticky Luxury Booking Summary ── */}
              <div className="bk-right-pane">
                <div className="bk-summary-card">
                  <div className="bk-summary-accent-bar" />

                  <div className="bk-summary-header">
                    <span className="bk-sum-tag">✦ RESERVATION SUMMARY ✦</span>
                    <h2 className="bk-sum-title">Your Celebration</h2>
                  </div>

                  {/* Venue Details */}
                  <div className="bk-sum-venue-box">
                    <div className="bk-sum-theater-name">{theaterName}</div>
                    <div className="bk-sum-meta-row">
                      <MapPin size={13} style={{ color: "#C9A84C" }} />
                      <span>{venueLocation}</span>
                    </div>
                    <div className="bk-sum-meta-row">
                      <Calendar size={13} style={{ color: "#C9A84C" }} />
                      <span>{slotDate}</span>
                    </div>
                    <div className="bk-sum-meta-row">
                      <Clock size={13} style={{ color: "#C9A84C" }} />
                      <span>{slotTime}</span>
                    </div>
                    <div className="bk-sum-meta-row">
                      <Users size={13} style={{ color: "#C9A84C" }} />
                      <span>{guestsCount} Guests ({maxPeopleIncluded} Included)</span>
                    </div>
                  </div>

                  {/* Selections Breakdown */}
                  <div className="bk-sum-custom-section">
                    <div className="bk-sum-section-lbl">Selected Inclusions</div>

                    <div className="bk-sum-line-item">
                      <span className="bk-line-name">
                        {selectedOccasion?.name || "Occasion"}
                      </span>
                      <span className="bk-line-status">
                        {celebrantName ? celebrantName : "Selected"}
                      </span>
                    </div>

                    <div className="bk-sum-line-item">
                      <span className="bk-line-name">Celebration Cake</span>
                      <span className="bk-line-status">
                        {selectedCake && !isCakeSkipped
                          ? `${selectedCake.name} (${cakeWeight === "500" ? "500g" : cakeWeight + "kg"})`
                          : isCakeSkipped
                            ? "Skipped"
                            : "None"}
                      </span>
                    </div>

                    <div className="bk-sum-line-item">
                      <span className="bk-line-name">Experiences / Add-Ons</span>
                      <span className="bk-line-status">
                        {selectedAddons.length > 0
                          ? `${selectedAddons.length} Selected`
                          : "None"}
                      </span>
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="bk-sum-price-section">
                    <div className="bk-sum-price-row">
                      <span>Base Theatre Experience</span>
                      <span>₹{baseTheaterPrice}</span>
                    </div>

                    {extraPersonsCost > 0 && (
                      <div className="bk-sum-price-row">
                        <span>Extra Guests ({extraPersonsCount} × ₹{extraPersonPrice})</span>
                        <span>₹{extraPersonsCost}</span>
                      </div>
                    )}

                    {calculatedCakePrice > 0 && (
                      <div className="bk-sum-price-row">
                        <span>Cake ({cakeWeight === "500" ? "500g" : cakeWeight + "kg"})</span>
                        <span>₹{calculatedCakePrice}</span>
                      </div>
                    )}

                    {addonsTotal > 0 && (
                      <div className="bk-sum-price-row">
                        <span>Selected Add-Ons</span>
                        <span>₹{addonsTotal}</span>
                      </div>
                    )}

                    {packageDiscount > 0 && (
                      <div className="bk-sum-price-row bk-discount-row">
                        <span>Package Discount</span>
                        <span>− ₹{packageDiscount}</span>
                      </div>
                    )}

                    <div className="bk-sum-price-row">
                      <span>CGST (9%)</span>
                      <span>+ ₹{cgstAmount.toFixed(2)}</span>
                    </div>

                    <div className="bk-sum-price-row">
                      <span>SGST (9%)</span>
                      <span>+ ₹{sgstAmount.toFixed(2)}</span>
                    </div>

                    <div className="bk-sum-divider" />

                    <div className="bk-sum-total-row">
                      <span className="bk-total-lbl">Total Booking Amount</span>
                      <span className="bk-total-val">₹{finalTotal.toFixed(2)}</span>
                    </div>

                    <div className="bk-advance-highlight-box">
                      <div className="bk-adv-line">
                        <span>Advance Payable Now</span>
                        <strong className="bk-adv-num">₹{advanceAmount.toFixed(2)}</strong>
                      </div>
                      <div className="bk-rem-line">
                        <span>Balance at Venue</span>
                        <span>₹{remainingAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Guarantee note */}
                  <div className="bk-guarantee-strip">
                    <ShieldCheck size={16} style={{ color: "#C9A84C", flexShrink: 0 }} />
                    <span>Private 4K Screening · 100% Privacy Guaranteed · Instant Slot Lock</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* ── UPI QR Modal ── */}
        {showQRModal && (
          <div className="bk-qr-modal-backdrop">
            <div className="bk-qr-modal-card">
              <button
                type="button"
                className="bk-qr-close"
                onClick={() => {
                  setShowQRModal(false);
                  navigate("/payment-success");
                }}
              >
                <X size={18} />
              </button>

              <div className="bk-qr-header">
                <span className="bk-qr-tag">SCAN & PAY ADVANCE</span>
                <h3 className="bk-qr-title">Complete Your Reservation</h3>
                <p className="bk-qr-sub">
                  Scan the UPI QR code using Google Pay, PhonePe, Paytm, or any UPI app to pay ₹{advanceAmount}.
                </p>
              </div>

              <div className="bk-qr-img-box">
                {qrImageUrl ? (
                  <img src={qrImageUrl} alt="UPI Payment QR" className="bk-qr-img" />
                ) : (
                  <div className="bk-qr-placeholder">
                    <QrCode size={96} style={{ color: "#C9A84C" }} />
                    <span>Generating payment code...</span>
                  </div>
                )}
              </div>

              {upiIntentUrl && (
                <a href={upiIntentUrl} className="bk-upi-intent-btn">
                  Open in UPI App
                </a>
              )}

              <button
                type="button"
                className="bk-qr-done-btn"
                onClick={() => {
                  setShowQRModal(false);
                  navigate("/payment-success");
                }}
              >
                I Have Completed the Payment
              </button>
            </div>
          </div>
        )}

        <Footer />
        <ToastContainer position="bottom-right" />
      </div>

      <style>{`
        /* ── Base Booking Page Tokens & Styling (Ultra-Luxury Editorial) ── */
        .bk-page-root {
          background-color: #FAF8F5;
          color: #261B2E;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        /* ── Stepper Navigation: Wide, Sculpted Luxury Bar ── */
        .bk-stepper-container {
          background: #FFFFFF;
          border-bottom: 1px solid #E6DDD1;
          padding: 18px 0 16px;
          position: sticky;
          top: 0;
          z-index: 90;
          box-shadow: 0 4px 20px rgba(22, 2, 34, 0.03);
        }

        .bk-stepper-inner {
          width: calc(100% - 48px);
          max-width: 1560px;
          margin: 0 auto;
          padding: 0;
        }

        .bk-step-badge-mobile {
          display: none;
        }

        .bk-desktop-stepper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
        }

        .bk-step-node {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          flex: 1;
        }

        .bk-step-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #F5EFE6;
          border: 1.5px solid #DCD1C2;
          color: #7A6D85;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
          transition: all 0.25s ease;
        }

        .bk-step-node.active .bk-step-circle {
          background-color: #170224;
          border-color: #C9A84C;
          color: #C9A84C;
          box-shadow: 0 0 0 4px rgba(201, 168, 76, 0.22);
        }

        .bk-step-node.completed .bk-step-circle {
          background-color: #C9A84C;
          border-color: #C9A84C;
          color: #FFFFFF;
        }

        .bk-step-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding-right: 14px;
        }

        .bk-step-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.8px;
          color: #6E6278;
          text-transform: uppercase;
        }

        .bk-step-node.active .bk-step-label {
          color: #170224;
        }

        .bk-step-sub {
          font-size: 11px;
          color: #9E92A7;
          white-space: nowrap;
        }

        .bk-step-node.active .bk-step-sub {
          color: #5D436A;
          font-weight: 500;
        }

        .bk-step-line {
          flex: 1;
          height: 1.5px;
          background-color: #E8E0D5;
          margin: 0 14px 0 0;
        }

        .bk-step-node.completed .bk-step-line {
          background-color: #C9A84C;
        }

        /* ── Workspace Grid: Wide, Fully Occupied, Tightened Paddings ── */
        .bk-main-wrap {
          padding: 22px 0 54px;
        }

        .bk-content-container {
          width: calc(100% - 48px);
          max-width: 1560px;
          margin: 0 auto;
          padding: 0;
        }

        .bk-workspace-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 420px;
          gap: 26px;
          align-items: start;
        }

        /* ── Step Content Card: Crisp, Refined, Less Bloat ── */
        .bk-card-step {
          background-color: #FFFFFF;
          border: 1px solid #E8E0D5;
          border-radius: 14px;
          padding: 28px 30px;
          box-shadow: 0 6px 24px rgba(23, 2, 36, 0.035);
        }

        .bk-step-header {
          margin-bottom: 22px;
          border-bottom: 1px solid #F2EBE2;
          padding-bottom: 16px;
        }

        .bk-eyebrow {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 2px;
          color: #B89535;
          text-transform: uppercase;
          display: inline-block;
          margin-bottom: 4px;
        }

        .bk-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 25px;
          font-weight: 700;
          color: #170224;
          margin: 0 0 5px;
          letter-spacing: -0.2px;
        }

        .bk-subtitle {
          font-size: 13.5px;
          color: #6A5F73;
          margin: 0;
          line-height: 1.5;
        }

        /* ── Form Inputs ── */
        .bk-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          margin-bottom: 22px;
        }

        .bk-form-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .bk-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #4D4357;
        }

        .bk-lbl-icon {
          color: #B89535;
        }

        .bk-req {
          color: #C02929;
        }

        .bk-input {
          width: 100%;
          background-color: #FAF8F5;
          border: 1.5px solid #E0D6C6;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13.5px;
          color: #170224;
          outline: none;
          transition: all 0.2s ease;
        }

        .bk-input:focus {
          border-color: #5E0E73;
          background-color: #FFFFFF;
          box-shadow: 0 0 0 3px rgba(94, 14, 115, 0.08);
        }

        .bk-input.error {
          border-color: #D32F2F;
          background-color: #FFF8F8;
        }

        .bk-err-msg {
          font-size: 11px;
          color: #D32F2F;
          font-weight: 500;
        }

        .bk-phone-wrap {
          display: flex;
          align-items: center;
          background-color: #FAF8F5;
          border: 1.5px solid #E0D6C6;
          border-radius: 8px;
          overflow: hidden;
        }

        .bk-phone-prefix {
          padding: 10px 12px;
          font-size: 13px;
          font-weight: 600;
          color: #7A6D85;
          background-color: #F2EBE0;
          border-right: 1px solid #E0D6C6;
        }

        .bk-input-phone {
          border: none;
          border-radius: 0;
          background: transparent;
        }

        .bk-input-phone:focus {
          box-shadow: none;
        }

        .bk-guest-counter {
          display: flex;
          align-items: center;
          background-color: #FAF8F5;
          border: 1.5px solid #E0D6C6;
          border-radius: 8px;
          overflow: hidden;
          height: 42px;
        }

        .bk-counter-btn {
          width: 42px;
          height: 100%;
          background-color: #F2EBE0;
          border: none;
          color: #170224;
          font-size: 17px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .bk-counter-btn:hover {
          background-color: #E6DCD0;
        }

        .bk-counter-val {
          flex: 1;
          text-align: center;
          font-size: 13.5px;
          font-weight: 700;
          color: #170224;
        }

        .bk-field-hint {
          font-size: 11px;
          color: #8C8095;
          line-height: 1.45;
        }

        /* ── Package Format Selector ── */
        .bk-package-section {
          padding: 16px 0;
          border-top: 1px solid #F0EAE1;
          margin-bottom: 20px;
        }

        .bk-pkg-label {
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #6A5F73;
          margin-bottom: 10px;
        }

        .bk-pkg-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .bk-pkg-card {
          background-color: #FAF8F5;
          border: 1.5px solid #E2D7C7;
          border-radius: 10px;
          padding: 14px 16px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .bk-pkg-card:hover {
          border-color: #C9A84C;
        }

        .bk-pkg-card.selected {
          border-color: #5E0E73;
          background-color: #FCF9FE;
          box-shadow: 0 4px 12px rgba(94, 14, 115, 0.05);
        }

        .bk-pkg-radio {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 1.5px solid #A89EB0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .bk-pkg-card.selected .bk-pkg-radio {
          border-color: #5E0E73;
        }

        .bk-radio-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #5E0E73;
        }

        .bk-pkg-info {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .bk-pkg-name {
          font-size: 13.5px;
          font-weight: 700;
          color: #170224;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .bk-pkg-pill {
          font-size: 9.5px;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: #170224;
          background-color: #ECD9AC;
          padding: 2px 7px;
          border-radius: 4px;
        }

        .bk-pkg-desc {
          font-size: 12px;
          color: #6E6278;
          line-height: 1.45;
        }

        /* ── Occasion Image Cards ── */
        .bk-occasions-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-bottom: 22px;
        }

        .bk-occ-card {
          background: #FFFFFF;
          border: 1.5px solid #E2D7C7;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
        }

        .bk-occ-card:hover {
          transform: translateY(-3px);
          border-color: #C9A84C;
          box-shadow: 0 8px 20px rgba(201, 168, 76, 0.14);
        }

        .bk-occ-card.selected {
          border-color: #5E0E73;
          box-shadow: 0 0 0 2px rgba(94, 14, 115, 0.18), 0 8px 20px rgba(94, 14, 115, 0.1);
        }

        /* Image container: 4:3 aspect, full fill */
        .bk-occ-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: #F0EBF4;
        }

        .bk-occ-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          transition: transform 0.22s ease;
        }

        .bk-occ-card:hover .bk-occ-img {
          transform: scale(1.04);
        }

        .bk-occ-title {
          font-size: 12.5px;
          font-weight: 700;
          color: #170224;
          text-align: center;
          padding: 10px 8px;
          line-height: 1.25;
          letter-spacing: 0.1px;
        }

        .bk-occ-card.selected .bk-occ-title {
          color: #5E0E73;
        }

        .bk-occ-check {
          position: absolute;
          top: 7px;
          right: 7px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #5E0E73;
          color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(94,14,115,0.4);
        }

        .bk-celebrant-input-box {
          background-color: #FAF8F5;
          border: 1px solid #E2D7C7;
          border-radius: 10px;
          padding: 16px 18px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 22px;
        }

        /* ── Cake Controls & 4-Column Wide Grid (NO PRICES) ── */
        .bk-cake-controls-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 18px;
        }

        .bk-cake-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .bk-cake-tab {
          background-color: #FFFFFF;
          border: 1.5px solid #D8CFC2;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 12.5px;
          font-weight: 600;
          color: #4B4154;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .bk-cake-tab:hover {
          border-color: #5E0E73;
          color: #5E0E73;
        }

        .bk-cake-tab.active {
          background-color: #170224;
          color: #FFFFFF;
          border-color: #170224;
          box-shadow: 0 3px 10px rgba(23, 2, 36, 0.18);
        }

        .bk-skip-cake-btn {
          background-color: #FFFFFF;
          border: 1.5px dashed #B8ACB8;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 12.5px;
          font-weight: 600;
          color: #6C6275;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .bk-skip-cake-btn:hover {
          border-color: #170224;
          color: #170224;
        }

        .bk-skip-cake-btn.active {
          background-color: #F8F4EC;
          border-color: #C9A84C;
          color: #8A6D24;
          border-style: solid;
        }

        .bk-cakes-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 18px;
          margin-bottom: 24px;
        }

        .bk-cake-card {
          background-color: #FFFFFF;
          border: 1.5px solid #EAE3D8;
          border-radius: 16px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          padding: 12px;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 4px 14px rgba(23, 2, 36, 0.04);
          position: relative;
        }

        .bk-cake-card:hover {
          transform: translateY(-4px);
          border-color: #C9A84C;
          box-shadow: 0 12px 28px rgba(94, 14, 115, 0.12);
        }

        .bk-cake-card.selected {
          border-color: #5E0E73;
          background-color: #FCF9FE;
          box-shadow: 0 10px 26px rgba(94, 14, 115, 0.16);
          outline: 2px solid #5E0E73;
          outline-offset: -2px;
        }

        .bk-cake-thumb-wrap {
          position: relative;
          height: 180px;
          background: radial-gradient(circle at center, #FFFFFF 45%, #F7F3EC 100%);
          border: 1px solid #ECE3D4;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          overflow: hidden;
        }

        .bk-cake-thumb {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          object-position: center;
          filter: drop-shadow(0 5px 12px rgba(0, 0, 0, 0.09));
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .bk-cake-card:hover .bk-cake-thumb {
          transform: scale(1.05);
        }

        .bk-veg-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background-color: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(6px);
          padding: 3.5px 8px;
          border-radius: 6px;
          font-size: 9.5px;
          font-weight: 700;
          color: #167A33;
          border: 1px solid rgba(22, 122, 51, 0.28);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
          display: inline-flex;
          align-items: center;
          gap: 3px;
          letter-spacing: 0.2px;
          z-index: 2;
        }

        .bk-cake-selected-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          background: linear-gradient(135deg, #5E0E73 0%, #8A12A8 100%);
          color: #FFFFFF;
          padding: 3.5px 8px;
          border-radius: 6px;
          font-size: 9.5px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 4px;
          box-shadow: 0 3px 10px rgba(94, 14, 115, 0.32);
          z-index: 2;
        }

        .bk-cake-body {
          padding: 12px 2px 2px 2px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .bk-cake-name {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 14.5px;
          font-weight: 700;
          color: #170224;
          margin: 0 0 10px;
          line-height: 1.35;
          min-height: 39px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bk-cake-weight-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          margin-bottom: 11px;
          background-color: #FAF7F2;
          border: 1px solid #E5DCCE;
          border-radius: 8px;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .bk-cake-weight-row:hover,
        .bk-cake-weight-row:focus-within {
          border-color: #C9A84C;
          background-color: #FFFFFF;
        }

        .bk-weight-lbl {
          font-size: 11px;
          font-weight: 600;
          color: #72657C;
          margin: 0;
        }

        .bk-weight-select {
          border: none;
          background: transparent;
          font-size: 12px;
          font-weight: 700;
          color: #170224;
          outline: none;
          cursor: pointer;
        }

        .bk-cake-choose-btn {
          width: 100%;
          background: linear-gradient(180deg, #F8F4EE 0%, #EFE7DA 100%);
          border: 1.5px solid #D8CDBE;
          border-radius: 8px;
          padding: 8.5px 12px;
          font-size: 12px;
          font-weight: 700;
          color: #2D143E;
          cursor: pointer;
          transition: all 0.22s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          letter-spacing: 0.2px;
        }

        .bk-cake-choose-btn:hover {
          background: #5E0E73;
          border-color: #5E0E73;
          color: #FFFFFF;
          box-shadow: 0 4px 12px rgba(94, 14, 115, 0.25);
        }

        .bk-cake-choose-btn.chosen {
          background: linear-gradient(135deg, #170224 0%, #3B094A 100%);
          color: #FFFFFF;
          border-color: #170224;
          box-shadow: 0 4px 14px rgba(23, 2, 36, 0.25);
        }

        .bk-skipped-cake-notice {
          background-color: #FAF8F5;
          border: 1px dashed #C9A84C;
          border-radius: 12px;
          padding: 24px 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 22px;
        }

        .bk-skipped-cake-notice strong {
          display: block;
          font-size: 14.5px;
          color: #170224;
          margin-bottom: 4px;
        }

        .bk-skipped-cake-notice p {
          margin: 0;
          font-size: 13px;
          color: #6E6278;
        }

        /* ── Add-Ons: 4-Column Grid on Desktop, 2-Column on Mobile ── */
        .bk-addons-list {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 22px;
        }

        @media (max-width: 991px) {
          .bk-addons-list {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
        }

        @media (max-width: 575px) {
          .bk-addons-list {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
        }

        .bk-addon-card {
          background-color: #FFFFFF;
          border: 2px solid #C69FF4;
          border-radius: 12px;
          padding: 10px;
          display: flex;
          flex-direction: column;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .bk-addon-card:hover {
          border-color: #8C44D4;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(140, 68, 212, 0.12);
        }

        .bk-addon-card.selected {
          background-color: #F6EDFC;
          border-color: #8C44D4;
          box-shadow: 0 4px 14px rgba(94, 14, 115, 0.15);
        }

        .bk-addon-category-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: #300843;
          margin: 28px 0 16px 0;
          padding-left: 2px;
        }

        .bk-addon-img-wrap {
          width: 100%;
          aspect-ratio: 16 / 11;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
          margin-bottom: 10px;
        }

        .bk-addon-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }

        .bk-addon-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .bk-addon-name {
          font-size: 13.5px;
          font-weight: 700;
          color: #170224;
          margin: 0 0 6px 0;
          line-height: 1.3;
          text-align: left;
        }

        .bk-addon-bottom-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: auto;
          padding-top: 6px;
        }

        .bk-badge-free {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: #1E7E34;
          background-color: #E8F7EC;
          border: 1px solid #B8E4C3;
          padding: 2px 6px;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .bk-addon-price-tag {
          font-size: 14px;
          font-weight: 700;
          color: #5E0E73;
        }

        .bk-addon-toggle-btn {
          padding: 5px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          background-color: #FFFFFF;
          border: 1.5px solid #8C44D4;
          color: #8C44D4;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .bk-addon-toggle-btn.added {
          background-color: #8C44D4;
          color: #FFFFFF;
          border-color: #8C44D4;
        }

        /* ── Step 5: Terms & Review Details ── */
        .bk-review-overview-card {
          background-color: #FAF8F5;
          border: 1px solid #E2D7C7;
          border-radius: 12px;
          padding: 18px 20px;
          margin-bottom: 18px;
        }

        .bk-rev-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .bk-rev-item {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .bk-rev-lbl {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #8C8095;
        }

        .bk-rev-val {
          font-size: 13.5px;
          font-weight: 700;
          color: #170224;
        }

        .bk-rev-sub {
          font-size: 11.5px;
          color: #6E6278;
        }

        .bk-heard-box {
          margin-bottom: 18px;
        }

        .bk-clean-select {
          width: 100%;
          background-color: #FAF8F5;
          border: 1.5px solid #E2D7C7;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13.5px;
          color: #170224;
          outline: none;
          cursor: pointer;
        }

        .bk-terms-container {
          background-color: #FAF8F5;
          border: 1px solid #E2D7C7;
          border-radius: 12px;
          padding: 18px 20px;
          margin-bottom: 20px;
        }

        .bk-terms-title {
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #554D5D;
          margin-bottom: 8px;
        }

        .bk-terms-scroll-area {
          max-height: 160px;
          overflow-y: auto;
          background-color: #FFFFFF;
          border: 1px solid #EAE1D5;
          border-radius: 8px;
          padding: 12px 14px;
          font-size: 12px;
          color: #554D5D;
          line-height: 1.55;
          margin-bottom: 12px;
        }

        .bk-terms-default-list p {
          margin: 0 0 8px;
        }

        .bk-terms-default-list p:last-child {
          margin: 0;
        }

        .bk-agree-label {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 12px;
          color: #3B3342;
          cursor: pointer;
        }

        .bk-agree-checkbox {
          width: 16px;
          height: 16px;
          accent-color: #5E0E73;
          margin-top: 1px;
          cursor: pointer;
        }

        /* ── Step Navigation Buttons ── */
        .bk-step-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding-top: 18px;
          border-top: 1px solid #F0EAE1;
        }

        .bk-btn-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background-color: #FAF8F5;
          border: 1px solid #D8CFC2;
          border-radius: 8px;
          padding: 10px 18px;
          font-size: 13px;
          font-weight: 600;
          color: #382D42;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .bk-btn-back:hover {
          background-color: #EFE8DC;
        }

        .bk-btn-continue {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background-color: #170224;
          border: 1px solid #170224;
          border-radius: 8px;
          padding: 11px 24px;
          font-size: 13.5px;
          font-weight: 700;
          color: #FFFFFF;
          cursor: pointer;
          transition: all 0.22s ease;
        }

        .bk-btn-continue:hover {
          background-color: #5E0E73;
          border-color: #C9A84C;
          box-shadow: 0 4px 14px rgba(94, 14, 115, 0.25);
        }

        .bk-btn-confirm {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background-color: #170224;
          border: 1px solid #C9A84C;
          border-radius: 8px;
          padding: 12px 26px;
          font-size: 13.5px;
          font-weight: 700;
          color: #FAF7F2;
          cursor: pointer;
          transition: all 0.22s ease;
        }

        .bk-btn-confirm:hover:not(:disabled) {
          background-color: #3A0552;
          color: #FFFFFF;
          box-shadow: 0 6px 18px rgba(94, 14, 115, 0.3);
        }

        .bk-btn-confirm.disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        /* ── Sticky Luxury Summary Panel (Right Column) ── */
        .bk-right-pane {
          position: sticky;
          top: 76px;
        }

        .bk-summary-card {
          background-color: #FFFFFF;
          border: 1px solid #E8E0D5;
          border-radius: 14px;
          padding: 22px 20px;
          box-shadow: 0 6px 24px rgba(23, 2, 36, 0.045);
          position: relative;
        }

        .bk-summary-accent-bar {
          position: absolute;
          top: 0;
          left: 20px;
          right: 20px;
          height: 3px;
          background-color: #C9A84C;
          border-radius: 0 0 4px 4px;
        }

        .bk-summary-header {
          text-align: center;
          margin-bottom: 14px;
        }

        .bk-sum-tag {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #B89535;
          text-transform: uppercase;
          display: inline-block;
          margin-bottom: 3px;
        }

        .bk-sum-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 19px;
          font-weight: 700;
          color: #170224;
          margin: 0;
        }

        .bk-sum-venue-box {
          background-color: #FAF8F5;
          border: 1px solid #E5DCD0;
          border-radius: 8px;
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 14px;
        }

        .bk-sum-theater-name {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 14.5px;
          font-weight: 700;
          color: #170224;
          margin-bottom: 2px;
        }

        .bk-sum-meta-row {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 11.5px;
          color: #6A5F73;
        }

        .bk-sum-custom-section {
          padding: 10px 0;
          border-top: 1px solid #F0EAE1;
          border-bottom: 1px solid #F0EAE1;
          display: flex;
          flex-direction: column;
          gap: 7px;
          margin-bottom: 14px;
        }

        .bk-sum-section-lbl {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #8C8095;
        }

        .bk-sum-line-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12px;
          gap: 10px;
        }

        .bk-line-name {
          color: #554D5D;
          font-weight: 500;
        }

        .bk-line-status {
          color: #170224;
          font-weight: 600;
          text-align: right;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bk-sum-price-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 14px;
        }

        .bk-sum-price-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12px;
          color: #685E72;
        }

        .bk-discount-row {
          color: #1E7E34;
          font-weight: 600;
        }

        .bk-sum-divider {
          height: 1px;
          background-color: #EAE1D5;
          margin: 4px 0;
        }

        .bk-sum-total-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 14.5px;
          font-weight: 700;
          color: #170224;
          margin-bottom: 6px;
        }

        .bk-total-val {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 17.5px;
          color: #5E0E73;
        }

        .bk-advance-highlight-box {
          background-color: #FAF4FD;
          border: 1px solid #EDDDF6;
          border-radius: 8px;
          padding: 9px 12px;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .bk-adv-line {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12px;
          font-weight: 600;
          color: #5E0E73;
        }

        .bk-adv-num {
          font-size: 13.5px;
          font-weight: 800;
        }

        .bk-rem-line {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 11px;
          color: #7D7087;
        }

        .bk-guarantee-strip {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 10.5px;
          color: #6C6275;
          line-height: 1.4;
          background-color: #F8F5F0;
          border-radius: 6px;
          padding: 7px 10px;
        }

        /* ── UPI QR Modal ── */
        .bk-qr-modal-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(23, 2, 36, 0.68);
          backdrop-filter: blur(6px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .bk-qr-modal-card {
          background-color: #FFFFFF;
          border-radius: 16px;
          padding: 30px 26px;
          max-width: 420px;
          width: 100%;
          text-align: center;
          position: relative;
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
        }

        .bk-qr-close {
          position: absolute;
          top: 14px;
          right: 14px;
          background: transparent;
          border: none;
          color: #7D7087;
          cursor: pointer;
        }

        .bk-qr-tag {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #B89535;
          text-transform: uppercase;
        }

        .bk-qr-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 21px;
          font-weight: 700;
          color: #170224;
          margin: 4px 0 6px;
        }

        .bk-qr-sub {
          font-size: 12.5px;
          color: #6E6278;
          line-height: 1.45;
          margin-bottom: 18px;
        }

        .bk-qr-img-box {
          width: 190px;
          height: 190px;
          margin: 0 auto 18px;
          border: 1px solid #E2D7C7;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #FAF8F5;
          overflow: hidden;
        }

        .bk-qr-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .bk-qr-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #8C8095;
        }

        .bk-upi-intent-btn {
          display: block;
          width: 100%;
          background-color: #5E0E73;
          color: #FFFFFF;
          padding: 11px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          text-decoration: none;
          margin-bottom: 10px;
        }

        .bk-qr-done-btn {
          width: 100%;
          background-color: #170224;
          color: #FFFFFF;
          border: none;
          padding: 11px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        /* ── Responsive Adaptations ── */
        @media (max-width: 1280px) {
          .bk-cakes-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .bk-addons-list {
            grid-template-columns: repeat(3, 1fr);
            gap: 14px;
          }
          .bk-rev-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 991px) {
          .bk-workspace-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .bk-right-pane {
            position: static;
            order: 2;
          }

          .bk-occasions-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .bk-cakes-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .bk-addons-list {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
        }

        @media (max-width: 767px) {
          .bk-stepper-container {
            padding: 12px 0;
          }

          .bk-desktop-stepper {
            display: none;
          }

          .bk-step-badge-mobile {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .bk-mb-step {
            font-size: 11px;
            font-weight: 700;
            color: #C9A84C;
            letter-spacing: 1px;
            text-transform: uppercase;
          }

          .bk-mb-name {
            font-size: 13px;
            font-weight: 700;
            color: #170224;
          }

          .bk-stepper-inner,
          .bk-content-container {
            width: calc(100% - 28px);
          }

          .bk-card-step {
            padding: 20px 16px;
          }

          .bk-form-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }

          .bk-pkg-grid {
            grid-template-columns: 1fr;
          }

          .bk-occasions-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .bk-cakes-grid {
            grid-template-columns: 1fr;
          }

          .bk-addons-list {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .bk-rev-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
        }

        @media (max-width: 480px) {
          .bk-addons-list {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
        }

          .bk-step-nav {
            flex-direction: column-reverse;
          }

          .bk-btn-back, .bk-btn-continue, .bk-btn-confirm {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
}